import React, { useState } from 'react';
import { Zap, TrendingUp, Award, Target, CheckCircle, Brain, BookOpen, Code, Rocket, Users, Clock } from 'lucide-react';

const AdvancedCourses = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const courses = [
    {
      title: "JEE Advanced Mastery",
      category: "competitive",
      level: "Advanced",
      description: "Intensive preparation for IIT-JEE Advanced with focus on complex problem-solving, advanced concepts, and time management strategies",
      modules: ["Advanced Calculus", "Complex Numbers & Trigonometry", "Organic Mechanisms", "Modern Physics", "3D Geometry", "Thermodynamics"],
      eligibility: "JEE Main Qualified / Class 12",
      duration: "12 Months"
    },
    {
      title: "NEET Advanced Plus",
      category: "competitive",
      level: "Advanced",
      description: "Comprehensive NEET preparation covering entire syllabus with emphasis on NCERT mastery, clinical reasoning, and time management",
      modules: ["Human Physiology", "Organic Chemistry", "Genetics & Evolution", "Plant Physiology", "Biomolecules", "Modern Physics"],
      eligibility: "Class 12 / Droppers",
      duration: "12 Months"
    },
    {
      title: "IIT-JAM Advanced",
      category: "postgraduate",
      level: "Advanced",
      description: "Specialized coaching for IIT-JAM across Mathematics, Physics, Chemistry with research-oriented approach",
      modules: ["Real Analysis", "Linear Algebra", "Quantum Mechanics", "Statistical Mechanics", "Inorganic Chemistry", "Physical Chemistry"],
      eligibility: "B.Sc / B.Tech Students",
      duration: "10 Months"
    },
    {
      title: "GATE Excellence Program",
      category: "postgraduate",
      level: "Advanced",
      description: "Branch-specific GATE preparation with focus on numerical problems, previous year analysis, and PSU interview prep",
      modules: ["Engineering Mathematics", "Core Subjects", "Aptitude & Reasoning", "Previous Year Solutions", "Mock Test Series"],
      eligibility: "B.Tech 3rd/4th Year",
      duration: "12 Months"
    },
    {
      title: "GRE/GMAT Preparation",
      category: "abroad",
      level: "Advanced",
      description: "Complete preparation for graduate admissions tests with focus on verbal reasoning, quantitative aptitude, and analytical writing",
      modules: ["Verbal Reasoning", "Quantitative Reasoning", "Analytical Writing", "Test Strategies", "Time Management", "Full-Length Mocks"],
      eligibility: "Graduates / Working Professionals",
      duration: "4-6 Months"
    },
    {
      title: "IELTS/TOEFL Mastery",
      category: "abroad",
      level: "Advanced",
      description: "Intensive English proficiency preparation for international education and immigration with native speaker training",
      modules: ["Reading Comprehension", "Listening Skills", "Speaking Practice", "Writing Tasks", "Grammar Perfection", "Mock Tests"],
      eligibility: "Study Abroad Aspirants",
      duration: "3-4 Months"
    },
    {
      title: "Advanced Data Science",
      category: "professional",
      level: "Advanced",
      description: "Master machine learning, deep learning, and AI with hands-on projects and industry-relevant case studies",
      modules: ["Machine Learning", "Deep Learning", "NLP", "Computer Vision", "Big Data", "MLOps"],
      eligibility: "Engineering Students/Graduates",
      duration: "6 Months"
    },
    {
      title: "Full Stack Development",
      category: "professional",
      level: "Advanced",
      description: "Complete web development bootcamp covering frontend, backend, databases, and deployment with real projects",
      modules: ["React/Angular", "Node.js", "MongoDB/SQL", "REST APIs", "Cloud Deployment", "System Design"],
      eligibility: "Basic Programming Knowledge",
      duration: "6 Months"
    },
    {
      title: "Competitive Programming",
      category: "professional",
      level: "Advanced",
      description: "Master algorithms and data structures for coding interviews at top tech companies and competitive contests",
      modules: ["Advanced DSA", "Dynamic Programming", "Graph Algorithms", "System Design", "Problem Solving", "Contest Strategies"],
      eligibility: "Good Programming Skills",
      duration: "8 Months"
    },
    {
      title: "Research Methodology",
      category: "academic",
      level: "Advanced",
      description: "Essential skills for research scholars including paper writing, literature review, statistical analysis, and presentation",
      modules: ["Research Design", "Literature Review", "Statistical Analysis", "Paper Writing", "Data Visualization", "Presentation Skills"],
      eligibility: "PG Students / Researchers",
      duration: "4 Months"
    },
    {
      title: "Mathematics Olympiad Advanced",
      category: "competitive",
      level: "Advanced",
      description: "Preparation for INMO, IMO with focus on advanced problem-solving, mathematical reasoning, and proof techniques",
      modules: ["Number Theory", "Combinatorics", "Geometry", "Algebra", "Problem Solving", "Proof Techniques"],
      eligibility: "RMO Qualified / Class 10-12",
      duration: "10 Months"
    },
    {
      title: "Physics Olympiad Advanced",
      category: "competitive",
      level: "Advanced",
      description: "INPhO and IPhO preparation with advanced theoretical physics and experimental problem-solving",
      modules: ["Classical Mechanics", "Electrodynamics", "Quantum Mechanics", "Thermodynamics", "Special Relativity", "Experimental Methods"],
      eligibility: "NSEP Qualified / Class 11-12",
      duration: "10 Months"
    }
  ];

  const features = [
    {
      icon: Brain,
      title: "Expert Faculty",
      description: "Learn from IIT/IIM alumni and industry professionals with years of teaching and practical experience"
    },
    {
      icon: Target,
      title: "Personalized Learning",
      description: "Custom study plans, one-on-one mentorship, and focused attention on individual improvement areas"
    },
    {
      icon: Rocket,
      title: "Advanced Curriculum",
      description: "Cutting-edge syllabus covering latest exam patterns, industry trends, and research methodologies"
    },
    {
      icon: Award,
      title: "Proven Results",
      description: "Track record of top ranks in JEE, NEET, GATE, and successful placements at leading companies"
    }
  ];

  const benefits = [
    {
      title: "Small Batch Sizes",
      description: "Maximum 15-20 students per batch ensuring personalized attention and interactive learning environment",
      icon: Users
    },
    {
      title: "Comprehensive Study Material",
      description: "Detailed notes, practice problems, previous year papers, and exclusive content prepared by IIT alumni",
      icon: BookOpen
    },
    {
      title: "Regular Mock Tests",
      description: "Weekly tests, monthly assessments, and full-length mock exams with detailed performance analysis",
      icon: TrendingUp
    },
    {
      title: "Doubt Clearing Sessions",
      description: "Dedicated doubt clearing sessions, discussion forums, and 24/7 online support for queries",
      icon: Zap
    },
    {
      title: "Interview Preparation",
      description: "Mock interviews, personality development, and communication skills training for competitive exams",
      icon: Target
    },
    {
      title: "Industry Exposure",
      description: "Guest lectures, workshops, webinars with industry experts and successful alumni sharing experiences",
      icon: Code
    }
  ];

  const categories = [
    { value: 'all', label: 'All Courses' },
    { value: 'competitive', label: 'Competitive Exams' },
    { value: 'postgraduate', label: 'Postgraduate' },
    { value: 'abroad', label: 'Study Abroad' },
    { value: 'professional', label: 'Professional Skills' },
    { value: 'academic', label: 'Academic Research' }
  ];

  const filteredCourses = selectedCategory === 'all' 
    ? courses 
    : courses.filter(c => c.category === selectedCategory);

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', backgroundColor: '#fff' }}>
      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)',
        color: 'white',
        padding: '100px 20px 80px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 50% 50%, rgba(88, 169, 91, 0.15) 0%, transparent 50%)',
          animation: 'pulse 4s ease-in-out infinite'
        }} />
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '40px', alignItems: 'center' }}>
            <div>
              <h1 style={{
                fontSize: 'clamp(2rem, 5vw, 3rem)',
                fontWeight: 'bold',
                marginBottom: '1.5rem',
                animation: 'fadeInUp 0.8s ease-out',
                lineHeight: '1.2'
              }}>
                Advanced Courses
                <span style={{ color: '#58A95B', display: 'block' }}>Master Your Domain</span>
              </h1>
              <p style={{
                fontSize: 'clamp(1rem, 2vw, 1.2rem)',
                marginBottom: '2rem',
                lineHeight: '1.8',
                animation: 'fadeInUp 1s ease-out'
              }}>
                Elevate your skills with our advanced courses designed for serious aspirants. 
                From JEE Advanced to GATE, GRE to professional certifications - learn from 
                the best and achieve excellence.
              </p>
              <button style={{
                backgroundColor: '#58A95B',
                border: 'none',
                padding: '15px 40px',
                fontSize: '1.1rem',
                fontWeight: '600',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                animation: 'fadeInUp 1.2s ease-out'
              }}
              onMouseOver={(e) => e.target.style.transform = 'translateY(-3px)'}
              onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}>
                Explore Advanced Programs
              </button>
            </div>
            <div style={{ textAlign: 'center', animation: 'float 3s ease-in-out infinite' }}>
              <Zap size={200} color="#58A95B" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={{ padding: '80px 20px', backgroundColor: '#f8f9fa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '3rem',
            color: '#000000'
          }}>
            Why Choose Our <span style={{ color: '#58A95B' }}>Advanced Programs?</span>
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '30px'
          }}>
            {features.map((feature, idx) => {
              const IconComponent = feature.icon;
              return (
                <div key={idx} style={{
                  backgroundColor: 'white',
                  borderRadius: '15px',
                  padding: '30px 20px',
                  textAlign: 'center',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  boxShadow: '0 5px 20px rgba(0,0,0,0.08)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  e.currentTarget.style.boxShadow = '0 15px 40px rgba(88, 169, 91, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.08)';
                }}>
                  <IconComponent size={50} color="#58A95B" style={{ marginBottom: '20px' }} />
                  <h5 style={{ fontWeight: '600', marginBottom: '10px', color: '#000' }}>{feature.title}</h5>
                  <p style={{ color: '#666', lineHeight: '1.6', margin: 0 }}>{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section style={{ padding: '80px 20px', backgroundColor: '#ffffff' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '2rem',
            color: '#000000'
          }}>
            Our <span style={{ color: '#58A95B' }}>Advanced Courses</span>
          </h2>

          {/* Category Filter */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '10px',
            marginBottom: '3rem',
            flexWrap: 'wrap'
          }}>
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value)}
                style={{
                  padding: '10px 25px',
                  borderRadius: '25px',
                  border: '2px solid',
                  borderColor: selectedCategory === cat.value ? '#58A95B' : '#ddd',
                  backgroundColor: selectedCategory === cat.value ? '#58A95B' : 'white',
                  color: selectedCategory === cat.value ? 'white' : '#333',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  fontSize: '0.95rem'
                }}
                onMouseEnter={(e) => {
                  if (selectedCategory !== cat.value) {
                    e.target.style.borderColor = '#58A95B';
                    e.target.style.color = '#58A95B';
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedCategory !== cat.value) {
                    e.target.style.borderColor = '#ddd';
                    e.target.style.color = '#333';
                  }
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Courses Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '30px'
          }}>
            {filteredCourses.map((course, idx) => (
              <div key={idx} style={{
                backgroundColor: 'white',
                borderRadius: '15px',
                overflow: 'hidden',
                boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
                transition: 'all 0.3s ease',
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #f0f0f0'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 15px 40px rgba(88, 169, 91, 0.15)';
                e.currentTarget.style.borderColor = '#58A95B';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.08)';
                e.currentTarget.style.borderColor = '#f0f0f0';
              }}>
                <div style={{
                  background: 'linear-gradient(135deg, #000000 0%, #2a2a2a 100%)',
                  padding: '25px 20px',
                  color: 'white',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    backgroundColor: '#58A95B',
                    padding: '5px 15px',
                    borderRadius: '20px',
                    fontSize: '0.8rem',
                    fontWeight: '600'
                  }}>
                    {course.level}
                  </div>
                  <h4 style={{ fontWeight: 'bold', marginBottom: '8px', paddingRight: '100px' }}>{course.title}</h4>
                </div>

                <div style={{ padding: '25px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <p style={{ color: '#333', marginBottom: '20px', lineHeight: '1.6', fontSize: '0.95rem' }}>
                    {course.description}
                  </p>

                  <div style={{ marginBottom: '20px', flex: 1 }}>
                    <h6 style={{ fontWeight: '600', marginBottom: '12px', color: '#000', fontSize: '0.95rem' }}>
                      Key Topics:
                    </h6>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      {course.modules.slice(0, 6).map((module, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                          <CheckCircle size={14} color="#58A95B" style={{ marginRight: '8px', flexShrink: 0 }} />
                          <span style={{ color: '#666', fontSize: '0.85rem' }}>{module}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{
                    borderTop: '1px solid #eee',
                    paddingTop: '15px',
                    marginBottom: '15px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    fontSize: '0.9rem',
                    color: '#666'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span><strong>Eligibility:</strong> {course.eligibility}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Clock size={16} style={{ marginRight: '8px', color: '#58A95B' }} />
                      <span><strong>Duration:</strong> {course.duration}</span>
                    </div>
                  </div>

                  <button style={{
                    width: '100%',
                    backgroundColor: '#58A95B',
                    border: 'none',
                    padding: '12px',
                    fontWeight: '600',
                    borderRadius: '8px',
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#4a8f4d'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#58A95B'}>
                    Enroll Now
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section style={{ padding: '80px 20px', backgroundColor: '#f8f9fa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            textAlign: 'center',
            fontSize: 'clamp(1.8rem, 4vw, 2.5rem)',
            fontWeight: 'bold',
            marginBottom: '3rem',
            color: '#000000'
          }}>
            What You <span style={{ color: '#58A95B' }}>Get</span>
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '30px'
          }}>
            {benefits.map((benefit, idx) => {
              const IconComponent = benefit.icon;
              return (
                <div key={idx} style={{
                  padding: '25px',
                  borderRadius: '15px',
                  border: '2px solid #f0f0f0',
                  backgroundColor: 'white',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#58A95B';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#f0f0f0';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}>
                  <IconComponent size={35} color="#58A95B" style={{ marginBottom: '15px' }} />
                  <h5 style={{ fontWeight: '600', marginBottom: '10px', color: '#000' }}>{benefit.title}</h5>
                  <p style={{ color: '#666', lineHeight: '1.6', margin: 0, fontSize: '0.95rem' }}>{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '80px 20px',
        background: 'linear-gradient(135deg, #58A95B 0%, #4a8f4d 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: 'bold', marginBottom: '1.5rem' }}>
            Ready to Take the Next Step?
          </h2>
          <p style={{ fontSize: 'clamp(1rem, 2vw, 1.2rem)', marginBottom: '2rem', lineHeight: '1.6' }}>
            Join thousands of successful students who transformed their careers with our advanced courses. 
            Expert faculty, comprehensive curriculum, and proven results await you.
          </p>
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button style={{
              backgroundColor: '#000000',
              border: 'none',
              padding: '15px 40px',
              fontSize: '1.1rem',
              fontWeight: '600',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#1a1a1a';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = '#000000';
              e.target.style.transform = 'scale(1)';
            }}>
              Start Learning Today
            </button>
            <button style={{
              backgroundColor: 'transparent',
              border: '2px solid white',
              padding: '15px 40px',
              fontSize: '1.1rem',
              fontWeight: '600',
              borderRadius: '8px',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.color = '#58A95B';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = 'white';
            }}>
              Talk to Counselor
            </button>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 0.5;
          }
          50% {
            opacity: 0.8;
          }
        }

        * {
          box-sizing: border-box;
        }

        button {
          font-family: inherit;
        }

        @media (max-width: 768px) {
          section {
            padding: 60px 15px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default AdvancedCourses;